/**
 * DeviceController — orchestrates the state machine with fetch + timers +
 * DOM rendering. Mirrors apps/signage-tv/lib/state/device_controller.dart
 * in structure.
 *
 * Contract for the UI layer: subscribe() returns an unsubscribe function;
 * the callback fires on every state or observable-field change. The UI
 * inspects `state`, `config`, `sessionContent`, `orgContent`, `pairingCode`
 * and re-renders idempotently.
 */

import {
  NetworkError,
  PairClaimSuccess,
  PairPollResponse,
  PairStartResponse,
  SignageApi,
  SignageDeviceCommandAck,
  TokenRevoked,
  UpgradeRequired,
} from '../api.js';
import { createStorage, ensureDeviceCode, PlayerStorage, STORAGE_KEYS } from '../storage.js';
import { DeviceEvent, DeviceState, isPaired, transition } from './machine.js';

export interface ControllerOptions {
  readonly api: SignageApi;
  readonly storage?: PlayerStorage;
  readonly appVersion: string;
  /** Screen resolution reported at pair time. Pass overridden values for tests. */
  readonly screenResolution?: string;
  /** Browser label reported as os_version. */
  readonly browserLabel?: string;
  /** Poll interval guard used during pairing (milliseconds). Default 3000. */
  readonly pairPollBackoffMs?: number;
}

export interface UpgradeInfo {
  readonly minimumVersion?: string;
  readonly storeUrl?: string;
}

export type Listener = () => void;

export class DeviceController {
  readonly api: SignageApi;
  readonly storage: PlayerStorage;
  readonly appVersion: string;
  readonly screenResolution: string;
  readonly browserLabel: string;

  private _state: DeviceState = 'boot';
  private _token: string | null = null;
  private _deviceId: string | null = null;
  private _orgId: string | null = null;
  private _pairingCode: PairStartResponse | null = null;
  private _config: Record<string, unknown> | null = null;
  private _sessionContent: Record<string, unknown> | null = null;
  private _orgContent: Record<string, unknown> | null = null;
  private _lastError: string | null = null;
  private _upgradeInfo: UpgradeInfo | null = null;
  private _forcedMode: string | null = null;

  private pairAbort: AbortController | null = null;
  private pairPollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pairPollBackoffMs: number;
  private readonly listeners = new Set<Listener>();

  // Background loops. Each is null until paired and cleared on stop/wipe.
  private contentTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private commandLoopRunning = false;
  private commandLoopAbort: AbortController | null = null;
  private staleSweepTimer: ReturnType<typeof setInterval> | null = null;
  private lastContentSuccessAt: number | null = null;
  private errorCountSinceHeartbeat = 0;
  private rebootRequested = false;

  constructor(opts: ControllerOptions) {
    this.api = opts.api;
    this.storage = opts.storage ?? createStorage();
    this.appVersion = opts.appVersion;
    this.screenResolution =
      opts.screenResolution ?? `${window.screen?.width ?? 1920}x${window.screen?.height ?? 1080}`;
    this.browserLabel = opts.browserLabel ?? deriveBrowserLabel();
    this.pairPollBackoffMs = opts.pairPollBackoffMs ?? 3000;
  }

  /* ---------- observable accessors ---------- */

  get state(): DeviceState {
    return this._state;
  }
  get token(): string | null {
    return this._token;
  }
  get deviceId(): string | null {
    return this._deviceId;
  }
  get orgId(): string | null {
    return this._orgId;
  }
  get pairingCode(): PairStartResponse | null {
    return this._pairingCode;
  }
  get config(): Record<string, unknown> | null {
    return this._config;
  }
  get sessionContent(): Record<string, unknown> | null {
    return this._sessionContent;
  }
  get orgContent(): Record<string, unknown> | null {
    return this._orgContent;
  }
  get lastError(): string | null {
    return this._lastError;
  }
  get upgradeInfo(): UpgradeInfo | null {
    return this._upgradeInfo;
  }
  get forcedMode(): string | null {
    return this._forcedMode;
  }
  /** True between an ack'd reboot command and the actual page reload. */
  get isRebooting(): boolean {
    return this.rebootRequested;
  }
  get effectiveMode(): string {
    if (this._forcedMode) return this._forcedMode;
    const mode = this._config?.['mode'];
    return typeof mode === 'string' ? mode : 'idle';
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /* ---------- lifecycle ---------- */

  /**
   * Bootstrap from storage and route into pairing or rendering. Called once
   * from main.ts on page load.
   */
  async bootstrap(): Promise<void> {
    this._token = this.storage.get(STORAGE_KEYS.token);
    this._config = jsonOrNull(this.storage.get(STORAGE_KEYS.config));
    this._sessionContent = jsonOrNull(this.storage.get(STORAGE_KEYS.session));
    this._orgContent = jsonOrNull(this.storage.get(STORAGE_KEYS.org));
    const lastRaw = this.storage.get(STORAGE_KEYS.lastSuccessAt);
    this.lastContentSuccessAt = lastRaw ? Date.parse(lastRaw) || null : null;

    this.apply({ type: 'bootstrapComplete', hasToken: this._token !== null });

    if (!this._token) {
      await this.beginPairing();
    } else {
      // Paired: start loops immediately against cached content, then
      // refresh + heartbeat will pick up anything that changed.
      this.startBackgroundLoops();
    }
  }

  /** Start or restart the pairing flow. Called on boot-no-token and on unpair/wipe. */
  async beginPairing(): Promise<void> {
    this.apply({ type: 'pairingStarted' });
    const deviceCode = ensureDeviceCode(this.storage);
    try {
      const response = await this.api.pairStart({
        device_code: deviceCode,
        platform: 'web',
        screen_resolution: this.screenResolution,
        app_version: this.appVersion,
        os_version: this.browserLabel,
      });
      this._pairingCode = response;
      this._lastError = null;
      this.apply({ type: 'codeIssued' });
      this.schedulePollForClaim(deviceCode, response.code);
    } catch (err) {
      this._lastError = describeError(err);
      this.notify();
      // Retry pair/start after backoff — the user will see the error banner
      // with a stale code (or no code) during retries.
      this.pairPollTimer = setTimeout(() => this.beginPairing(), this.pairPollBackoffMs * 2);
    }
  }

  /**
   * Long-poll /pair/poll. The server holds the connection up to 25s. On
   * success we transition to `rendering`. On pending we re-enter the loop.
   * On AbortError we stop quietly (controller.stop() was called).
   */
  private schedulePollForClaim(deviceCode: string, code: string): void {
    this.pairAbort?.abort();
    this.pairAbort = new AbortController();
    const signal = this.pairAbort.signal;

    const run = async (): Promise<void> => {
      try {
        const result = await this.api.pairPoll({ device_code: deviceCode, code }, signal);
        if (signal.aborted) return;
        if (isPairClaimSuccess(result)) {
          this._token = result.device_session_token;
          this._deviceId = result.device_id;
          this._orgId = result.organization_id;
          this.storage.set(STORAGE_KEYS.token, result.device_session_token);
          this._pairingCode = null;
          this._lastError = null;
          this.apply({ type: 'paired' });
          this.startBackgroundLoops();
          return;
        }
        // still pending — brief backoff to avoid tight loop on servers that
        // return immediately instead of holding the connection.
        this.pairPollTimer = setTimeout(run, this.pairPollBackoffMs);
      } catch (err) {
        if (signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof TokenRevoked) {
          // Code was revoked server-side; mint a new one.
          this._lastError = 'Code expired — refreshing…';
          this.notify();
          this.pairPollTimer = setTimeout(() => this.beginPairing(), this.pairPollBackoffMs);
          return;
        }
        if (err instanceof NetworkError) {
          this._lastError = 'No connection — retrying…';
          this.notify();
          this.pairPollTimer = setTimeout(run, this.pairPollBackoffMs * 2);
          return;
        }
        if (err instanceof UpgradeRequired) {
          this.handleUpgradeRequired(err);
          return;
        }
        // Any other error — surface and retry.
        this._lastError = describeError(err);
        this.notify();
        this.pairPollTimer = setTimeout(run, this.pairPollBackoffMs * 2);
      }
    };

    void run();
  }

  private handleUpgradeRequired(err: UpgradeRequired): void {
    this._upgradeInfo = { minimumVersion: err.minimumVersion, storeUrl: err.storeUrl };
    this.apply({ type: 'upgradeRequired' });
    this.stop();
  }

  /**
   * Cancel all in-flight work. Used on unpair / upgrade / page unload.
   */
  stop(): void {
    this.pairAbort?.abort();
    this.pairAbort = null;
    if (this.pairPollTimer !== null) {
      clearTimeout(this.pairPollTimer);
      this.pairPollTimer = null;
    }
    this.stopBackgroundLoops();
  }

  /**
   * Wipe all session state and return to a fresh UnpairedScreen. Triggered
   * by tokenRevoked, admin unpair command, or 7-day unbound gate.
   */
  async wipe(): Promise<void> {
    this.stop();
    this.storage.wipe();
    this._token = null;
    this._deviceId = null;
    this._orgId = null;
    this._config = null;
    this._sessionContent = null;
    this._orgContent = null;
    this._pairingCode = null;
    this._forcedMode = null;
    this.lastContentSuccessAt = null;
    await this.beginPairing();
  }

  /* ---------- background loops ---------- */

  /**
   * Start the three long-lived loops (content refresh, heartbeat, command
   * long-poll) plus the stale-sweep watchdog. Idempotent.
   */
  private startBackgroundLoops(): void {
    if (!this._token) return;
    this.stopBackgroundLoops();

    // Kick off an immediate refresh so the UI upgrades from cache to live.
    void this.refreshContent();

    const rotation = this.rotationIntervalMs();
    this.contentTimer = setInterval(() => void this.refreshContent(), rotation);

    const heartbeat = this.heartbeatIntervalMs();
    this.heartbeatTimer = setInterval(() => void this.sendHeartbeat(), heartbeat);

    this.staleSweepTimer = setInterval(() => this.checkStaleGates(), 60_000);

    this.commandLoopAbort = new AbortController();
    if (!this.commandLoopRunning) {
      this.commandLoopRunning = true;
      void this.commandLoopBody(this.commandLoopAbort.signal);
    }
  }

  private stopBackgroundLoops(): void {
    if (this.contentTimer !== null) {
      clearInterval(this.contentTimer);
      this.contentTimer = null;
    }
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.staleSweepTimer !== null) {
      clearInterval(this.staleSweepTimer);
      this.staleSweepTimer = null;
    }
    this.commandLoopAbort?.abort();
    this.commandLoopAbort = null;
    this.commandLoopRunning = false;
  }

  private rotationIntervalMs(): number {
    const raw = this._config?.['rotation_interval_seconds'];
    const seconds = typeof raw === 'number' ? Math.min(5, Math.max(1, Math.floor(raw))) : 5;
    return seconds * 1000;
  }

  private heartbeatIntervalMs(): number {
    const raw = this._config?.['heartbeat_interval_seconds'];
    const seconds = typeof raw === 'number' && raw >= 10 ? Math.floor(raw) : 60;
    return seconds * 1000;
  }

  /* ---------- content refresh ---------- */

  private async refreshContent(): Promise<void> {
    if (!this._token) return;
    try {
      const [config, sessionContent, orgContent] = await Promise.all([
        this.api.getConfig(this._token),
        this.api.getSessionContent(this._token).catch(() => this._sessionContent ?? {}),
        this.api.getOrgContent(this._token).catch(() => this._orgContent ?? {}),
      ]);
      // CHG-3265 (QA #282): snapshot the observable payload BEFORE storing
      // the fresh fetch so we can detect a real content change below.
      const before = this.contentSnapshot();
      this._config = config ?? null;
      this._sessionContent = (sessionContent as Record<string, unknown>) ?? null;
      this._orgContent = (orgContent as Record<string, unknown>) ?? null;
      this._lastError = null;
      this.persistCache();
      this.markSuccess();
      this.apply({ type: 'contentReceived' });
      // CHG-3265 (QA #282): `apply` only notifies on a state-machine EDGE,
      // but contentReceived maps rendering → rendering (changed=false), so a
      // steady-state poll that fetched NEW content never reached subscribers
      // — the screen only updated on push commands or a full page reload.
      // Notify whenever the observable payload actually changed (change-
      // gated rather than unconditional so identical polls don't rebuild the
      // panel DOM every rotation interval; the Flutter port notifies on every
      // successful refresh, but its widget tree diffs — our renderers don't).
      if (this.contentSnapshot() !== before) {
        this.notify();
      }
      // Re-schedule loops if the server changed the rotation interval.
      if (this.contentTimer !== null) {
        const desired = this.rotationIntervalMs();
        clearInterval(this.contentTimer);
        this.contentTimer = setInterval(() => void this.refreshContent(), desired);
      }
    } catch (err) {
      if (err instanceof TokenRevoked) {
        this.apply({ type: 'tokenRevoked' });
        await this.wipe();
        return;
      }
      if (err instanceof UpgradeRequired) {
        this.handleUpgradeRequired(err);
        return;
      }
      this.errorCountSinceHeartbeat++;
      if (err instanceof NetworkError) {
        this.apply({ type: 'networkLost' });
      } else {
        this.apply({ type: 'contentFailed' });
      }
      this._lastError = describeError(err);
      this.notify();
    }
  }

  /**
   * Serialized view of everything a paired subscriber renders from.
   * Used by refreshContent (CHG-3265) to decide whether a successful poll
   * carried an observable change that warrants a repaint.
   */
  private contentSnapshot(): string {
    return JSON.stringify([this._config, this._sessionContent, this._orgContent, this._lastError]);
  }

  private persistCache(): void {
    if (this._config) this.storage.set(STORAGE_KEYS.config, JSON.stringify(this._config));
    if (this._sessionContent)
      this.storage.set(STORAGE_KEYS.session, JSON.stringify(this._sessionContent));
    if (this._orgContent) this.storage.set(STORAGE_KEYS.org, JSON.stringify(this._orgContent));
  }

  private markSuccess(): void {
    const now = Date.now();
    this.lastContentSuccessAt = now;
    this.storage.set(STORAGE_KEYS.lastSuccessAt, new Date(now).toISOString());
    if (this._state === 'offline' || this._state === 'stale') {
      this.apply({ type: 'networkRestored' });
    }
  }

  /* ---------- heartbeat ---------- */

  private async sendHeartbeat(): Promise<void> {
    if (!this._token) return;
    const payload = {
      current_mode: this.effectiveMode,
      app_version: this.appVersion,
      os_version: this.browserLabel,
      last_render_ts: new Date(this.lastContentSuccessAt ?? Date.now()).toISOString(),
      error_count_since_last: this.errorCountSinceHeartbeat,
    };
    try {
      await this.api.heartbeat(this._token, payload);
      this.errorCountSinceHeartbeat = 0;
    } catch (err) {
      if (err instanceof TokenRevoked) {
        this.apply({ type: 'tokenRevoked' });
        await this.wipe();
        return;
      }
      if (err instanceof UpgradeRequired) {
        this.handleUpgradeRequired(err);
        return;
      }
      this.errorCountSinceHeartbeat++;
    }
  }

  /* ---------- command long-poll ---------- */

  private async commandLoopBody(signal: AbortSignal): Promise<void> {
    while (!signal.aborted && this._token) {
      try {
        const commands = await this.api.getCommands(this._token, signal);
        if (signal.aborted) return;
        for (const cmd of commands) {
          await this.handleCommand(cmd);
          if (signal.aborted) return;
        }
      } catch (err) {
        if (signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof TokenRevoked) {
          this.apply({ type: 'tokenRevoked' });
          await this.wipe();
          return;
        }
        if (err instanceof UpgradeRequired) {
          this.handleUpgradeRequired(err);
          return;
        }
        // Transient — backoff and loop.
        await delay(3000);
      }
    }
    this.commandLoopRunning = false;
  }

  /** Route a single command through the appropriate handler + ack. */
  async handleCommand(cmd: Record<string, unknown>): Promise<void> {
    const id = typeof cmd['id'] === 'string' ? (cmd['id'] as string) : '';
    const type = typeof cmd['type'] === 'string' ? (cmd['type'] as string) : '';
    const payload = (cmd['payload'] as Record<string, unknown> | undefined) ?? {};
    if (!id) return;

    // Ack-first for destructive commands so the server records delivery even
    // if the client reload/wipe races the ack. refresh/force_mode ack AFTER
    // doing the work so the server sees the new state.
    try {
      switch (type) {
        case 'refresh':
          await this.refreshContent();
          this._forcedMode = null;
          await this.ackSafely(id, ackSuccess());
          this.notify();
          return;
        case 'force_mode': {
          const mode = typeof payload['mode'] === 'string' ? (payload['mode'] as string) : null;
          if (!mode) {
            await this.ackSafely(id, ackFailure('missing_mode'));
            return;
          }
          this._forcedMode = mode;
          await this.ackSafely(id, ackSuccess());
          this.notify();
          return;
        }
        case 'reboot':
          await this.ackSafely(id, ackSuccess());
          this.rebootRequested = true;
          this.notify();
          // Schedule reload after a tick so the subscriber has a chance to
          // paint a "Rebooting…" banner first. In browser terms a reload
          // is the closest analog to a device power-cycle.
          setTimeout(() => {
            try {
              window.location.reload();
            } catch {
              /* jsdom / non-browser: swallow */
            }
          }, 500);
          return;
        case 'unpair':
          await this.ackSafely(id, ackSuccess());
          await this.wipe();
          return;
        default:
          await this.ackSafely(id, ackNoop('unknown_type'));
          return;
      }
    } catch (err) {
      // Ack itself failed — swallow and let the server re-deliver.
      void err;
    }
  }

  private async ackSafely(id: string, result: SignageDeviceCommandAck): Promise<void> {
    if (!this._token) return;
    try {
      await this.api.ackCommand(this._token, id, result);
    } catch {
      // Transient ack failure — server will re-deliver the command.
    }
  }

  /* ---------- stale gates ---------- */

  private checkStaleGates(): void {
    if (this.lastContentSuccessAt === null) return;
    const ageMs = Date.now() - this.lastContentSuccessAt;
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    if (ageMs >= SEVEN_DAYS) {
      this.apply({ type: 'unboundBreached' });
      void this.wipe();
      return;
    }
    if (ageMs >= TWENTY_FOUR_HOURS && this._state === 'offline') {
      this.apply({ type: 'staleBreached' });
    }
  }

  /* ---------- test hooks ---------- */

  /** @internal test-only accessor so unit tests can drive a single command. */
  async debugRunCommand(cmd: Record<string, unknown>): Promise<void> {
    await this.handleCommand(cmd);
  }

  /** @internal test-only accessor so unit tests can inject a paired token. */
  debugInjectSession(token: string, deviceId: string, orgId: string): void {
    this._token = token;
    this._deviceId = deviceId;
    this._orgId = orgId;
    this.storage.set(STORAGE_KEYS.token, token);
    this.apply({ type: 'paired' });
  }

  /* ---------- internal ---------- */

  private apply(event: DeviceEvent): void {
    const result = transition(this._state, event);
    this._state = result.state;
    if (result.changed || this._pairingCode !== null || this._lastError !== null) {
      this.notify();
    }
  }

  private notify(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch {
        // Listeners must not throw out; a broken subscriber shouldn't break
        // the rest of the notification chain.
      }
    }
  }
}

/* ---------------- helpers ---------------- */

function jsonOrNull(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function isPairClaimSuccess(result: PairPollResponse): result is PairClaimSuccess {
  return (
    'device_id' in result &&
    typeof result.device_id === 'string' &&
    'device_session_token' in result &&
    typeof result.device_session_token === 'string' &&
    'organization_id' in result &&
    typeof result.organization_id === 'string' &&
    'expires_at' in result &&
    typeof result.expires_at === 'string'
  );
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'unexpected error';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function ackSuccess(): SignageDeviceCommandAck {
  return { result: 'success', executed_at: new Date().toISOString() };
}

function ackFailure(error: string): SignageDeviceCommandAck {
  return { result: 'failure', error_message: error, executed_at: new Date().toISOString() };
}

function ackNoop(reason: string): SignageDeviceCommandAck {
  return { result: 'noop', error_message: reason, executed_at: new Date().toISOString() };
}

function deriveBrowserLabel(): string {
  if (typeof navigator === 'undefined') return 'web/unknown';
  const ua = navigator.userAgent ?? '';
  // Very coarse — precise UA sniffing is brittle, and the backend only
  // stores this as a free-text hint in heartbeats.
  if (ua.includes('Chrome/')) {
    const m = ua.match(/Chrome\/([\d.]+)/);
    return `chrome/${m?.[1] ?? 'unknown'}`;
  }
  if (ua.includes('Firefox/')) {
    const m = ua.match(/Firefox\/([\d.]+)/);
    return `firefox/${m?.[1] ?? 'unknown'}`;
  }
  if (ua.includes('Safari/')) {
    const m = ua.match(/Version\/([\d.]+)/);
    return `safari/${m?.[1] ?? 'unknown'}`;
  }
  return 'web/unknown';
}

export function isPairedState(state: DeviceState): boolean {
  return isPaired(state);
}
